/** Footer — preserves blueprint structure: brand, contact, link columns, newsletter, legal. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Facebook, Instagram, Youtube, Twitter, ArrowRight, Check } from "lucide-react";
import { Brand } from "../ui/Brand";
import { api } from "../../lib/api";

const COLS = [
  {
    h: "Learn",
    links: [
      ["Courses", "/courses"],
      ["Sadhana", "/sadhana"],
      ["Library", "/library"],
      ["Research", "/research"],
      ["Certificates", "/dashboard/certificates"],
    ],
  },
  {
    h: "Explore",
    links: [
      ["Products", "/products"],
      ["Services", "/services"],
      ["Bookings", "/services"],
      ["Wishlist", "/dashboard/wishlist"],
    ],
  },
  {
    h: "Account",
    links: [
      ["Sign in", "/login"],
      ["Create account", "/register"],
      ["My dashboard", "/dashboard"],
      ["Privacy", "/privacy"],
      ["Terms", "/terms"],
    ],
  },
];

export function Footer() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    try { await api.post("/newsletter/subscribe", { email, source: "footer" }); setState("done"); setEmail(""); }
    catch { setState("error"); }
  };
  return (
    <footer
      className="relative z-[1] pt-16 pb-10 mt-16 border-t border-line"
      style={{ background: "linear-gradient(180deg, transparent, var(--bg-deep))" }}
      data-testid="site-footer"
    >
      {/* Mahamrityunjaya Mantra — scrolling marquee above the footer content */}
      <div className="max-w-[1280px] mx-auto px-4 mb-10 overflow-hidden" data-testid="mahamrityunjaya-strip">
        <div className="nvl-mantra-track text-[11.5px] text-text-faint font-mono tracking-[0.32em] uppercase">
          <span className="nvl-mantra-row">
            <span className="mx-8 text-mauve/80">॥</span>
            <span className="mx-8">Om Tryambakam Yajamahe</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Sugandhim Pushtivardhanam</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Urvarukamiva Bandhanan</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Mrityor Mukshiya Maamritat</span>
            <span className="mx-8 text-mauve/80">॥</span>
          </span>
          <span className="nvl-mantra-row" aria-hidden="true">
            <span className="mx-8 text-mauve/80">॥</span>
            <span className="mx-8">Om Tryambakam Yajamahe</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Sugandhim Pushtivardhanam</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Urvarukamiva Bandhanan</span>
            <span className="mx-8 text-mauve/70">·</span>
            <span className="mx-8">Mrityor Mukshiya Maamritat</span>
            <span className="mx-8 text-mauve/80">॥</span>
          </span>
        </div>
      </div>
      <div className="nvl-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand column */}
          <div className="md:col-span-4">
            <Brand />
            <p className="mt-6 text-text-dim text-[14.5px] leading-relaxed max-w-[36ch]">
              A research-grade spiritual university dedicated to preserving, teaching and advancing
              the sacred sciences for the modern world.
            </p>
            <div className="mt-6 flex flex-col gap-2 text-[13px] text-text-dim">
              <span className="inline-flex items-center gap-2">
                <MapPin size={14} /> Haridwar, Uttarakhand
              </span>
              <span className="inline-flex items-center gap-2">
                <Phone size={14} /> +91 82188 54187
              </span>
              <span className="inline-flex items-center gap-2">
                <Mail size={14} /> nikhilvatikaharidwar@gmail.com
              </span>
            </div>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <a href="https://www.youtube.com/@nikhilvatikaharidwar" target="_blank" rel="noopener noreferrer"
                 aria-label="YouTube — @nikhilvatikaharidwar" data-testid="footer-youtube-haridwar"
                 className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-line-2 bg-surface text-[12px] text-text-dim hover:text-mauve hover:border-mauve transition-colors">
                <span className="w-6 h-6 rounded-full grid place-items-center bg-red-600 text-white"><Youtube size={13} /></span>
                @nikhilvatikaharidwar
              </a>
              <a href="https://www.youtube.com/@nikhilvatikabyyogi" target="_blank" rel="noopener noreferrer"
                 aria-label="YouTube — @nikhilvatikabyyogi" data-testid="footer-youtube-yogi"
                 className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-line-2 bg-surface text-[12px] text-text-dim hover:text-mauve hover:border-mauve transition-colors">
                <span className="w-6 h-6 rounded-full grid place-items-center bg-red-600 text-white"><Youtube size={13} /></span>
                @nikhilvatikabyyogi
              </a>
            </div>
            <div className="mt-4 flex items-center gap-2">
              {[Facebook, Instagram, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label={Icon.displayName || "social"}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-line-2 bg-surface text-text-dim hover:text-mauve hover:border-mauve transition-colors"
                  data-testid={`footer-social-${i}`}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {COLS.map((col) => (
            <div key={col.h} className="md:col-span-2" data-testid={`footer-col-${col.h.toLowerCase()}`}>
              <h4 className="text-[13px] font-mono uppercase tracking-[0.2em] text-text-faint mb-5">
                {col.h}
              </h4>
              <ul className="flex flex-col gap-3 text-[14px]">
                {col.links.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-text-dim hover:text-mauve transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div className="md:col-span-2">
            <h4 className="text-[13px] font-mono uppercase tracking-[0.2em] text-text-faint mb-5">
              Stay on the Path
            </h4>
            <p className="text-text-dim text-[13.5px] mb-4">
              Join our newsletter for new courses, rare texts and teachings from the lineage.
            </p>
            <form
              className="relative"
              onSubmit={onSubmit}
              data-testid="footer-newsletter-form"
            >
              <input
                type="email"
                placeholder="Enter your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email address"
                className="w-full px-4 py-3 pr-12 rounded-full bg-surface border border-line-2 text-[13.5px] text-text placeholder:text-text-faint focus:border-mauve focus:outline-none transition-colors"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                aria-label="Subscribe"
                disabled={state === "loading"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full text-white inline-flex items-center justify-center disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, var(--plum-bright), var(--plum))" }}
                data-testid="footer-newsletter-submit"
              >
                {state === "done" ? <Check size={15} /> : <ArrowRight size={15} />}
              </button>
              {state === "done" && (
                <p className="mt-3 text-[12.5px] text-mauve inline-flex items-center gap-1.5" data-testid="footer-newsletter-success">
                  <Check size={13} /> Subscribed — welcome to the path.
                </p>
              )}
              {state === "error" && <p className="mt-2 text-[12px] text-red-400" data-testid="footer-newsletter-error">Couldn&apos;t subscribe — please try again.</p>}
            </form>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-line flex flex-wrap items-center justify-between gap-4 text-[12.5px] text-text-faint">
          <p>© 2026 Nikhil Vatika Lab · nikhilvatikalab.com · All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-mauve">Privacy</Link>
            <Link to="/terms" className="hover:text-mauve">Terms</Link>
            <Link to="/refund-policy" className="hover:text-mauve">Refund Policy</Link>
            <Link to="/accessibility" className="hover:text-mauve">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
